import React, { useEffect, useMemo, useState, useDeferredValue, useTransition, memo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  TextField,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Tooltip,
  Collapse,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import AppTopBar from '../../app-bar/AppBar';
import Footer from '../../footer/Footer';
import ContentModal from '../../content-modal/ContentModal';
import { PAGES } from '../../../constants';
import { CYBER_GLOSSARY } from '../../../constants/cyberGlossary';
import { ExtraControlsConfig } from '../../../types';
import { QLIK_APP_REF } from '../../../constants/qlik';
import { getHyperCubeData, getQlikApp } from '../../../services/qlikService';
import { useReady } from '../../../hooks/useReady';
import { RootState } from '../../../store/store';
import { gtm } from '../../../services/gtm';
import { PictureAsPdf } from '@mui/icons-material';

interface ThoughtLeadershipLink {
  type: string;
  title: string;
  link: string;
}

const CyberGlossary: React.FC = () => {
  const hyperCubeCustomIdIndSizeTLInsights = "cyber-glossary|ind-size-tl-insights";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const page = useMemo(() => PAGES['cyber-library']?.contents ?? {}, []);

  // State
  const [pageTitle] = useState(page.title?.text || '');
  const [pageSubtitle] = useState(page.sub?.text || '');
  const [initialized, setInitialized] = useState(false);

  // Panel expansion state moved into child components

  // Search states moved into child components

  // Modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  // Thought Leadership data from Qlik
  const [thoughtLeadershipLinks, setThoughtLeadershipLinks] = useState<ThoughtLeadershipLink[]>([]);

  // Qlik HyperCube
  const hyperCubetIndSizeTLInsights = useReady(QLIK_APP_REF, hyperCubeCustomIdIndSizeTLInsights).hyperCubeDataCustomId;
  const thoughtLeadershipHyperCubeData = useSelector(
    (state: RootState) =>
      state.qlikObject.qlikHyperCubesData[QLIK_APP_REF]?.[hyperCubeCustomIdIndSizeTLInsights]
  );

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized && !hyperCubetIndSizeTLInsights) {
      getIndustrySizeTLInsights();
    }
  }, [initialized, hyperCubetIndSizeTLInsights]);

  useEffect(() => {
    if (thoughtLeadershipHyperCubeData) {
      const matrix = thoughtLeadershipHyperCubeData.qDataPages[0].qMatrix;
      const links: ThoughtLeadershipLink[] = matrix
        .filter((row: any) => row[2]?.qText !== '-')
        .map((row: any) => ({
          type: row[0]?.qText || '',
          title: row[1]?.qText || '',
          link: row[2]?.qText || '',
        }));

      setThoughtLeadershipLinks(links);
    }
  }, [thoughtLeadershipHyperCubeData]);

  const getIndustrySizeTLInsights = async () => {
    try {
      const app = await getQlikApp(QLIK_APP_REF);
      if (!app) return;

      const indSizeTLInsightsHyperCubeDef = {
        qInitialDataFetch: [
          {
            qTop: 0,
            qLeft: 0,
            qHeight: 99,
            qWidth: 4,
          },
        ],
        qDimensions: [
          {
            qDef: {
              qFieldDefs: ['IndustrySizeTLType'],
            },
          },
          {
            qDef: {
              qFieldDefs: ['IndustrySizeTLTitle'],
            },
          },
          {
            qDef: {
              qFieldDefs: ['IndustrySizeTLLink'],
            },
          },
        ],
      };

      await getHyperCubeData(
        QLIK_APP_REF,
        hyperCubeCustomIdIndSizeTLInsights,
        indSizeTLInsightsHyperCubeDef
      );
    } catch (error) {
      console.error('Error creating Thought Leadership hypercube:', error);
    }
  };

  const handleVideoClick = (src: string, title: string) => {
    setVideoSrc(src);
    setVideoTitle(title);
    setVideoModalOpen(true);
    gtm.chartInteraction('Cyber Video Library', title);
  };

  const trackSearch = (section: string, keyword: string) => {
    if (keyword) {
      gtm.formEvent(`Search: ${keyword}`, section);
    }
  };

  const handleLinkClick = (linkName: string) => {
    gtm.chartInteraction('Cyber Library Link', linkName);
  };

  // Child component for Video Library (isolates its own state & renders)
  const VideoLibraryPanel: React.FC = () => {
    const [searchVid, setSearchVid] = useState('');
    const deferredSearchVid = useDeferredValue(searchVid);
    const filteredVideos = useMemo(() => {
      return CYBER_GLOSSARY.videoLibrary
        .filter((video) => video.name.toLowerCase().includes(deferredSearchVid.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [deferredSearchVid]);
    return (
      <Card sx={{ borderTop: 3, borderColor: 'primary.main', borderRadius: 0, mb: 3, position: 'relative' }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" component="h4">Cyber Video & Podcast Library</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name"
                value={searchVid}
                onChange={(e) => setSearchVid(e.target.value)}
                onBlur={() => trackSearch('Cyber Video Library', searchVid)}
                sx={{ mb: 3 }}
                slotProps={{
                    input: {
                        endAdornment: searchVid && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchVid('')}><ClearIcon /></IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
              />
              {/* {deferredSearchVid !== searchVid && (
                <Typography variant="caption" color="text.secondary">Updating results...</Typography>
              )} */}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                {filteredVideos.map((video, idx) => (
                  <Grid size={{ xs: 12, sm: 6, md: 6 }} key={idx}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                      onClick={() => handleVideoClick(video.src, video.name)}
                    >
                      <PlayCircleOutlineIcon color="primary" />
                      <Typography variant="body2">{video.name}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {filteredVideos.length === 0 && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">No items to display, clear search and try again</Typography>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Optimized GlossaryPanel (debounced search + memoized categories)
  const GlossaryPanel: React.FC = () => {
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [isPending, setIsPending] = useState(false);

    const glossaryPreprocessed = useMemo(() => {
      return CYBER_GLOSSARY.glossary.sub.map((category) => ({
        name: category.name,
        info: category.info,
        sub: category.sub.map((term: any) => ({ ...term, nameLC: term.name.toLowerCase() })),
      }));
    }, []);

    useEffect(() => {
      setIsPending(true);
      const handle = setTimeout(() => {
        setSearch(searchInput.trim().toLowerCase());
        setIsPending(false);
      }, 180);
      return () => clearTimeout(handle);
    }, [searchInput]);

    const filteredGlossary = useMemo(() => {
      if (!search) return glossaryPreprocessed;
      return glossaryPreprocessed
        .map((category) => ({
          ...category,
          sub: category.sub.filter((term: any) => term.nameLC.includes(search)),
        }))
        .filter((category) => category.sub.length > 0);
    }, [search, glossaryPreprocessed]);

    const GlossaryCategory = memo(({ category }: { category: any }) => (
      <Grid size={{ xs: 12, md: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6">{category.name}</Typography>
            <Tooltip title={category.info} placement="top">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
          {category.sub.map((term: any) => (
            <Accordion 
                key={term.name}
                slotProps={{ transition: { unmountOnExit: true }}}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{term.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="body2">{term.info}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Grid>
    ));
    GlossaryCategory.displayName = 'GlossaryCategory';

    return (
      <Card sx={{ borderTop: 3, borderColor: 'primary.main', borderRadius: 0, mb: 3, position: 'relative' }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" component="h4">{CYBER_GLOSSARY.glossary.name}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>{CYBER_GLOSSARY.glossary.info}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search term by name"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onBlur={() => trackSearch('Cyber Glossary', searchInput)}
                sx={{ mb: 3 }}
                slotProps={{
                  input: {
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchInput('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              {/* {isPending && <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>Filtering terms...</Typography>} */}
              <Grid container spacing={2}>
                {filteredGlossary.map((category) => (
                  <GlossaryCategory key={category.name} category={category} />
                ))}
              </Grid>
              {filteredGlossary.length === 0 && !isPending && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">No items to display, clear search and try again</Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const InsightsPanel: React.FC = () => {
    const cyberInsights = CYBER_GLOSSARY.cyberInsights.sub
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return (
      <Card sx={{ borderTop: 3, borderColor: 'primary.main', borderRadius: 0, mb: 3, position: 'relative' }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" component="h4">{CYBER_GLOSSARY.cyberInsights.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                {cyberInsights.map((insight, idx) => (
                    <Grid size={{ xs: 12, md: 4 }} key={idx}>
                        <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>{insight.name}</Typography></AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" sx={{ mb: 2 }}>{insight.info}</Typography>
                            {insight.sub.map((detail: string, detailIdx: number) => (
                            <Typography key={detailIdx} variant="body2" sx={{ mb: 1, pl: 2 }}>• {detail}</Typography>
                            ))}
                        </AccordionDetails>
                        </Accordion>
                    </Grid>
                ))}
                </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const ThoughtLeadershipPanel: React.FC = () => {
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [isPending, startTransition] = useTransition();

    const filteredThoughtLeadership = useMemo(() => {
      return thoughtLeadershipLinks.filter((link) =>
        link.title.toLowerCase().includes(deferredSearch.toLowerCase())
      );
    }, [deferredSearch, thoughtLeadershipLinks]);

    return (
      <Card sx={{ borderTop: 3, borderColor: 'primary.main', borderRadius: 0, mb: 3, position: 'relative' }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" component="h4">Thought Leadership</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search link by name"
                value={search}
                onChange={(e) => startTransition(() => setSearch(e.target.value))}
                onBlur={() => trackSearch('Thought Leadership', search)}
                sx={{ mb: 3 }}
                slotProps={{
                  input: {
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              {/* {isPending && <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>Filtering links...</Typography>} */}
              <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'stretch' }}>
                {filteredThoughtLeadership.map((link, idx) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx} sx={{ display: 'flex' }}>
                    <Link href={link.link} target="_blank" rel="noopener noreferrer" underline="none" onClick={() => handleLinkClick(link.title)} sx={{ width: '100%', display: 'flex' }}>
                      <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' }, display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {/* if link.qText = 'Web Page', then use openinnewicon, if link.qText = 'PDF', then use pdf icon */}
                        {link.type === 'PDF' ? (
                            <PictureAsPdf fontSize="small" color="primary" />
                        ) : (
                            <OpenInNewIcon fontSize="small" color="primary" />
                        )}
                        {/* <OpenInNewIcon color="primary" fontSize="small" /> */}
                        <Typography variant="body2" color="text.primary">{link.title}</Typography>
                      </Box>
                    </Link>
                  </Grid>
                ))}
              </Grid>
              {filteredThoughtLeadership.length === 0 && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">No items to display, clear search and try again</Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const UsefulLinksPanel: React.FC = () => {
    const links = CYBER_GLOSSARY.links
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return (
      <Card sx={{ borderTop: 3, borderColor: 'primary.main', borderRadius: 0, mb: 3, position: 'relative' }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }}>
            <Grid size={{ xs: 12 }}>
                <Typography variant="h6" component="h4">Useful Links</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <Grid container>
                    {links.map((link, idx) => (
                        <Grid size={{ xs: 12 }} key={idx}>
                            <Link href={link.url} target="_blank" rel="noopener noreferrer" underline="hover" onClick={() => handleLinkClick(link.name)} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                <OpenInNewIcon fontSize="small" color="primary" />
                                <Typography variant="body1">{link.name}</Typography>
                            </Link>
                        </Grid>
                    ))}
                </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
};

  // Extra controls config
  const extraControls: ExtraControlsConfig = {
    startOver: { enable: true },
    lastUpdate: { enable: true, componentLocation: 'nav' },
  };

  return (
    <>
      <AppTopBar extraControls={extraControls} filterElements={null} />

      <Grid
        container
        component="main"
        sx={{
          py: 2,
          px: { xs: 2, sm: 2, md: 2 },
          ml: { xs: 0, md: '57px' },
          transition: 'margin-left 0.3s',
        }}
      >
        {/* Title Section */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 400 }}>
              {pageTitle}
            </Typography>
            <Box sx={{ borderTop: 3, borderColor: 'primary.main', width: 60, mx: 'auto', mb: 2 }} />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 400 }}>
              {pageTitle}
            </Typography>
            <Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2 }}>
              <Typography variant="body1">{pageSubtitle}</Typography>
            </Box>
          </Box>
        </Grid>

        {/* Cyber Video & Podcast Library */}
        <Grid size={{ xs: 12 }}>
          <VideoLibraryPanel />
        </Grid>

        {/* Cyber Glossary */}
        <Grid size={{ xs: 12 }}>
          <GlossaryPanel />
        </Grid>

        {/* Cyber Threat Insights */}
        <Grid size={{ xs: 12 }}>
          <InsightsPanel />
        </Grid>

        {/* Thought Leadership */}
        <Grid size={{ xs: 12 }}>
          <ThoughtLeadershipPanel />
        </Grid>

        {/* Useful Links */}
        <Grid size={{ xs: 12 }}>
          <UsefulLinksPanel />
        </Grid>
      </Grid>

      <Footer componentLocation="dashboard" />

      {/* Video Modal */}
      <ContentModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        title={videoTitle}
        videoSrc={videoSrc}
        maxWidth="lg"
      />
    </>
  );
};

export default CyberGlossary;
